// Holds a point label to a line count before mapbox lays it out, judging lines by width alone.
// Mapbox balances its own breaks, so a rare label still lands one line past the estimate.

export interface ILabelParts {
  // Always shown whole.
  prefix: string
  // Joins prefix and title, and goes with the title when it is dropped.
  separator: string
  // The only part that is ever shortened.
  title: string
  // Always shown whole.
  suffix: string
}

const ELLIPSIS = '…'

const compose = (parts: ILabelParts, title: string = parts.title): string =>
  title
    ? parts.prefix + parts.separator + title + parts.suffix
    : parts.prefix + parts.suffix

// The label as the map shows it when nothing is cut.
export const labelPartsText = (parts: ILabelParts): string => compose(parts)

// The full label when it fits; otherwise the longest whole-word title that does, with an
// ellipsis, then a cut inside a first word too long on its own, then no title at all. Width only
// grows with the title, so each cut is a binary search.
export function capLabelLines(
  parts: ILabelParts,
  linesOf: (text: string) => number,
  maxLines: number,
): string {
  const fits = (text: string): boolean => linesOf(text) <= maxLines
  const full = compose(parts)
  if (fits(full)) return full

  const longest = (
    count: number,
    titleOf: (n: number) => string,
  ): string | undefined => {
    let low = 1
    let high = count
    let best: string | undefined
    while (low <= high) {
      const mid = Math.floor((low + high) / 2)
      const text = compose(parts, titleOf(mid).trimEnd() + ELLIPSIS)
      if (fits(text)) {
        best = text
        low = mid + 1
      } else {
        high = mid - 1
      }
    }
    return best
  }

  const words = parts.title.split(' ')
  const firstWord = Array.from(words[0])
  return (
    longest(words.length - 1, n => words.slice(0, n).join(' ')) ??
    longest(firstWord.length - 1, n => firstWord.slice(0, n).join('')) ??
    compose(parts, '')
  )
}
